import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Zoom from '@mui/material/Zoom';
import CustomToolTip from '../../../../ToolTipInfoCustomMUI';
import CompartmentTile from './CompartmentTile';
import {
	formatArea,
	renderExposition,
	calculateWindowAreaRatio,
	convertArrayToHoursRange2,
} from '../../../../../utils/formattingUtills';
import styles from '../styles/compartmentsGrid.module.scss';
import apart_styles from '../../../../../styles/aprt.module.scss';

/**
 * InfoMode Grid View - Grid-based compartments display with row-based expansion
 * Groups tiles into rows and inserts expansion between rows
 *
 * @param {Array} indoorCompartments - Indoor compartments data
 * @param {Array} outdoorCompartments - Outdoor compartments data
 * @param {Object} interactions - Interaction handlers from useCompartmentInteractions hook
 * @param {number} selectedCompartmentInd - Currently selected compartment index
 * @param {number} hoveredCompartmentInd - Currently hovered compartment index
 * @param {Function} getCompartmentName - Function to get compartment display name
 * @param {number} [columnsPerRow=3] - Number of columns in grid (default: 3)
 */
function InfoModeGridView({
	indoorCompartments,
	outdoorCompartments,
	interactions,
	selectedCompartmentInd,
	hoveredCompartmentInd,
	getCompartmentName,
	columnsPerRow = 3,
}) {
	const { t } = useTranslation();

	const { handleRoomHover, getClickHandler } = interactions;

	/**
	 * Group compartments into rows based on columnsPerRow
	 * Returns array of rows, each containing up to columnsPerRow compartments
	 */
	const groupIntoRows = useMemo(() => {
		return (compartments) => {
			const rows = [];
			for (let i = 0; i < compartments.length; i += columnsPerRow) {
				rows.push(compartments.slice(i, i + columnsPerRow));
			}
			return rows;
		};
	}, [columnsPerRow]);

	/**
	 * Find which row contains the selected compartment
	 */
	const findRowWithSelected = (rows) => {
		if (!selectedCompartmentInd) return -1;

		return rows.findIndex((row) =>
			row.some((compartment) => compartment.number === selectedCompartmentInd)
		);
	};

	/**
	 * Get the selected compartment object
	 */
	const getSelectedCompartment = (compartments) => {
		if (!selectedCompartmentInd) return null;
		return compartments.find((c) => c.number === selectedCompartmentInd);
	};

	/**
	 * Render expanded content for selected compartment
	 * This is where detailed info will be shown
	 */
	const renderExpandedContent = (compartment) => {
		return (
			<div className={styles.compartment_expanded_details}>
				{/* <h4>{getCompartmentName(compartment)}</h4> */}
				<div className={apart_styles.apartment_info_data_container}>
					<div className={apart_styles.apartment_info_data_element}>
						<span className={apart_styles.apartment_info_data_type}>
							{t('windows')}
							<CustomToolTip
								title={
									<>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('windows_info_1')}
										</p>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('windows_info_2')}
										</p>
									</>
								}
								slots={{
									transition: Zoom,
								}}
								arrow
							>
								<button className={apart_styles.infoToolTip__button}>?</button>
							</CustomToolTip>
						</span>
						<span className={apart_styles.apartment_info_data_value}>
							{calculateWindowAreaRatio(
								compartment.window_area,
								compartment.area_original,
								t
							) || t('lack_of')}
						</span>
					</div>
					<div className={apart_styles.apartment_info_data_element}>
						<span className={apart_styles.apartment_info_data_type}>
							{t('exposition')}
							<CustomToolTip
								title={
									<>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('exposition_info_1')}
										</p>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('exposition_info_2')}
										</p>
									</>
								}
								slots={{
									transition: Zoom,
								}}
								arrow
							>
								<button className={apart_styles.infoToolTip__button}>?</button>
							</CustomToolTip>
						</span>
						<span className={apart_styles.apartment_info_data_value}>
							{renderExposition(compartment.exposition, t)}
						</span>
					</div>
					<div className={apart_styles.apartment_info_data_element}>
						<span className={apart_styles.apartment_info_data_type}>
							{t('sun_hours')}
							{/* <CustomToolTip
								title={
									<>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('exposition_info_1')}
										</p>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('exposition_info_2')}
										</p>
									</>
								}
								slots={{
									transition: Zoom,
								}}
								arrow
							>
								<button className={apart_styles.infoToolTip__button}>?</button>
							</CustomToolTip> */}
						</span>
						<span className={apart_styles.apartment_info_data_value}>
							{convertArrayToHoursRange2(compartment.sun_light_ranges, t)}
						</span>
					</div>
				</div>
			</div>
		);
	};

	/**
	 * Render a single compartment tile (without expansion)
	 */
	const renderTile = (compartment, isOutdoor = false) => {
		const isSelected = selectedCompartmentInd === compartment.number;
		const isHovered = hoveredCompartmentInd === compartment.number;
		const clickHandler = getClickHandler(compartment.number);

		return (
			<CompartmentTile
				key={`${isOutdoor ? 'outdoor' : 'indoor'}_compartment_${
					compartment.number
				}`}
				compartment={compartment}
				isSelected={isSelected}
				isHovered={isHovered}
				onClick={clickHandler}
				onHover={() => handleRoomHover(compartment.number, false)}
				onHoverLeave={() => handleRoomHover(null)}
				labelText={getCompartmentName(compartment)}
				valueContent={
					<>
						{formatArea(compartment.area_original)} m<sup>2</sup>
					</>
				}
				expandedContent={null} // Don't use tile's built-in expansion
				hasTooltip={false}
				tooltipContent={null}
			/>
		);
	};

	/**
	 * Render rows of compartment tiles with expansion between rows
	 */
	const renderCompartmentRows = (compartments, isOutdoor = false) => {
		const rows = groupIntoRows(compartments);
		const selectedRowIndex = findRowWithSelected(rows);
		const selectedCompartment = getSelectedCompartment(compartments);

		return rows.map((row, rowIndex) => (
			<div key={`${isOutdoor ? 'outdoor' : 'indoor'}_row_${rowIndex}`}>
				{/* Render row of tiles */}
				<div className={styles.apartment_info_data_container}>
					{row.map((compartment) => renderTile(compartment, isOutdoor))}

					{/* Fill empty slots if row is not complete */}
					{row.length < columnsPerRow &&
						Array.from({ length: columnsPerRow - row.length }).map((_, i) => (
							<div
								key={`empty_${rowIndex}_${i}`}
								style={{ minHeight: '1px' }}
							/>
						))}
				</div>

				{/* Render expansion below this row if it contains selected tile */}
				{selectedRowIndex === rowIndex && selectedCompartment && (
					<div className={styles.compartment_row_expansion}>
						{renderExpandedContent(selectedCompartment)}
					</div>
				)}
			</div>
		));
	};

	return (
		<div className={styles.compartments_grid_container}>
			{/* Indoor Compartments Section */}
			{indoorCompartments.length > 0 && (
				<div className={styles.compartments_section}>
					{renderCompartmentRows(indoorCompartments, false)}
				</div>
			)}

			<hr />

			{/* Outdoor Compartments Section */}
			{outdoorCompartments.length > 0 && (
				<div className={styles.compartments_section}>
					{renderCompartmentRows(outdoorCompartments, true)}
				</div>
			)}

			<span style={{ fontSize: '0.85rem', color: '#666', display: 'block' }}>
				Podane wielkości stanowią wymiary projektowe, które na etapie realizacji
				mogą ulec zmianie.
			</span>
		</div>
	);
}

export default InfoModeGridView;
